import React, { useEffect, useState } from 'react';
import { Autocomplete, Button, Card, CloseIcon, Dialog, DialogContent, DialogTitle, FormHelperText, DialogActions, Fab, FormControl, InputAdornment, Grid, IconButton, InputLabel, Stack, TextField, Typography, VisibilityIcon, VisibilityOffIcon, EditIcon, AddIcon, styled } from "../../styles/MaterialUI";
import DashboardIcon from '@mui/icons-material/Dashboard';
import { OutlinedInput, Slide, Tooltip } from '@mui/material';
import apiConstants from '../../constants/apiConstants';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { showAlertPopup } from '../Redux/Features/showAlertSlice';
import ToolTip from '../../utils/CursorTooltip'
import { useOrgConfigData } from "../../hooks/useOrgConfigData";
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const IntegrationList = () => {
    const { userToken } = useSelector((state) => state.userToken);

    // ── Pull permitted_content_types from Redux (same source as CreateContentDialog) ──

    const orgConfig = useOrgConfigData("integrationList");
    const {
        org_properties: org_configurations,
        all_content_types,
        permitted_content_types
    } = orgConfig || {};

    const headers = { Authorization: `Bearer ${userToken}` };
    const [showPassword, setShowPassword] = useState(false);
    const [addIntegrationDialog, setAddIntegrationDialog] = useState(false);
    const [updateIntegrationDialog, setUpdateIntegrationDialog] = useState(false);
    const [orgIntegrationData, setOrgIntegrationData] = useState([]);
    const [credentialData, setCredentialData] = useState([]);
    const [orgIntegrationId, setOrgIntegrationId] = useState("");
    const [applicationName, setApplicationName] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [selectedOrgIntegrationId, setSelectedOrgIntegrationId] = useState("");
    const [applicationNameError, setApplicationNameError] = useState("");
    const [usernameError, setUsernameError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const dispatch = useDispatch();

    // ── Permitted integration names from Redux ─────────────────────────
    const permittedContentTypeNames = new Set(
        (permitted_content_types ?? []).map((p) => p.ic_content_type_name)
    );

    // ── Integration-flagged content types ──────────────────────────────
    const integrationFlaggedNames = new Set(
        (all_content_types ?? [])
            .filter((ct) => ct.ic_integration_flag === 1)
            .map((ct) => ct.ic_content_type_name)
    );

    // ── Build dropdown options ─────────────────────────────────────────

    // existing org integrations
    const orgIntegrationNames = new Set(
        (orgIntegrationData ?? []).map((i) => i.integration_name)
    );

    // ── Permitted integrations ─────────────────────────────────────────
    const permittedIntegrations = (orgIntegrationData ?? []).filter((i) => {
        return (
            i.ic_activeflag === 1 &&
            integrationFlaggedNames.has(i.integration_name) &&
            permittedContentTypeNames.has(i.integration_name)
        );
    });

    // ── Restricted integrations (from org data) ────────────────────────
    const restrictedFromOrg = (orgIntegrationData ?? []).filter((i) => {
        return (
            i.ic_activeflag === 0 ||
            !integrationFlaggedNames.has(i.integration_name) ||
            !permittedContentTypeNames.has(i.integration_name)
        );
    });

    // ── Missing flagged integrations (NOT in orgIntegrationData) ────────
    const missingFlaggedIntegrations = (all_content_types ?? [])
        .filter(
            (ct) =>
                ct.ic_integration_flag === 1 &&
                !orgIntegrationNames.has(ct.ic_content_type_name)
        )
        .map((ct) => ({
            integration_name: ct.ic_content_type_name,
            ic_org_integration_id: null, // or generate/handle as needed
            ic_activeflag: 0, // treat as restricted by default
        }));

    // ── Combine restricted ─────────────────────────────────────────────
    const restrictedIntegrations = [
        ...restrictedFromOrg,
        ...missingFlaggedIntegrations,
    ];

    // ── Final dropdown options ──────────────────────────────────────────
    const integrationOptions = [
        ...permittedIntegrations.map((i) => ({
            label: i.integration_name,
            ic_org_integration_id: i.ic_org_integration_id,
            disabled: false,
            disabledReason: null,
        })),

        ...restrictedIntegrations.map((i) => ({
            label: i.integration_name,
            ic_org_integration_id: i.ic_org_integration_id,
            disabled: true,
            disabledReason:
                i.ic_activeflag === 0
                    ? "Integration is not configured. Reach out to Support."
                    : !integrationFlaggedNames.has(i.integration_name)
                        ? "Not an integration type"
                        : "Content Type not permissioned. Reach out to Support.",
        })),
    ];
    const hanldeCloseIntegrationDialog = (_, reason) => {
        if (reason !== "backdropClick") {
            setAddIntegrationDialog(false);
            setUpdateIntegrationDialog(false);
            setOrgIntegrationId("");
            setApplicationName("");
            setUsername("");
            setPassword("");
            setSelectedOrgIntegrationId("");
            setShowPassword(false);
            setApplicationNameError("");
            setUsernameError("");
            setPasswordError("");
        }
    }

    const getIntegrations = async () => {
        try {
            const apiResponse = await axios.post(`${apiConstants.GET_API_URL}getIntegrations`, {}, { headers });
            const { data } = apiResponse.data;
            if (Array.isArray(data)) {
                setOrgIntegrationData(data);
            }
        } catch (error) {
            const { statusCode, message, redirect_url } = error.response.data;
            dispatch(showAlertPopup({ isOpen: true, severity: "error", message: message }));
            if (statusCode === 403) {
                window.open(redirect_url, "_self");
            }
        }
    }

    const getCredentials = async () => {
        try {
            const apiResponse = await axios.post(`${apiConstants.GET_API_URL}get_integration_details`, {}, { headers });
            const { data } = apiResponse.data;
            if (Array.isArray(data)) {
                setCredentialData(data);
            }
        } catch (error) {
            const { statusCode, message, redirect_url } = error.response.data;
            dispatch(showAlertPopup({ isOpen: true, severity: "error", message: message }));
            if (statusCode === 403) {
                window.open(redirect_url, "_self");
            }
        }
    }

    useEffect(() => {
        getIntegrations();
        getCredentials();
    }, []);

    const handleUpdateIntegrationDialog = async (data) => {
        const { ic_org_integration_id, integration_name } = data;
        const { username, password } = credentialData?.find(credential => credential.org_integration_id === ic_org_integration_id);

        setUsername(username);
        setPassword(password);
        setOrgIntegrationId(ic_org_integration_id);
        setApplicationName(integration_name);
        setUpdateIntegrationDialog(true);
    }

    const handleSaveIntegrations = async (e) => {
        e.preventDefault();
        const errors = {};

        if (!applicationName) {
            errors.applicationNameError = "Please select application name";
        }
        if (!username.trim()) {
            errors.usernameError = "Please enter the username";
        }
        if (!password.trim()) {
            errors.passwordError = "Please enter the password";
        }

        if (Object.keys(errors).length > 0) {
            setApplicationNameError(errors.applicationNameError || "");
            setUsernameError(errors.usernameError || "");
            setPasswordError(errors.passwordError || "");
            return;
        }

        const inputData = {
            "org_integration_id": selectedOrgIntegrationId, "username": username, "password": password
        }

        try {
            const response = await axios.post(`${apiConstants.GET_API_URL}add_update_integration`, inputData,
                { headers: { Authorization: `Bearer ${userToken}` } }
            );
            if (response.data.statusCode === 200) {
                hanldeCloseIntegrationDialog();
                dispatch(showAlertPopup({ isOpen: true, severity: "success", message: "Integration Added Successfully" }));
            }
            getIntegrations();
            getCredentials();
        } catch (error) {
            const { statusCode, message, redirect_url } = error.response.data;
            dispatch(showAlertPopup({ isOpen: true, severity: "error", message: message }));
            if (statusCode === 403) {
                window.open(redirect_url, "_self");
            }
        }
    }

    const handleUpdateIntegrations = async (e) => {
        e.preventDefault();
        const errors = {};

        if (!username.trim()) {
            errors.usernameError = "Please enter the username";
        }
        if (!password.trim()) {
            errors.passwordError = "Please enter the password";
        }

        if (Object.keys(errors).length > 0) {
            setUsernameError(errors.usernameError || "");
            setPasswordError(errors.passwordError || "");
            return;
        }

        const inputData = {
            "org_integration_id": orgIntegrationId, "username": username, "password": password
        }

        try {
            const response = await axios.post(`${apiConstants.GET_API_URL}add_update_integration`, inputData,
                { headers: { Authorization: `Bearer ${userToken}` } }
            );
            if (response.data.statusCode === 200) {
                hanldeCloseIntegrationDialog();
                dispatch(showAlertPopup({ isOpen: true, severity: "success", message: "Integration Updated Successfully" }));
            }
            getCredentials();
        } catch (error) {
            const { statusCode, message, redirect_url } = error.response.data;
            dispatch(showAlertPopup({ isOpen: true, severity: "error", message: message }));
            if (statusCode === 403) {
                window.open(redirect_url, "_self");
            }
        }
    }

    const StyledCard = styled(Card)({
        minHeight: "150px",
        overflow: "auto",
        padding: "5px",
        marginLeft: "10px",
        borderRadius: "16px",
        background: "radial-gradient(178.94% 106.41% at 26.42% 106.41%, #ff8000 0%, rgba(255, 255, 255, 0) 71.88%)",
        boxShadow: "0 6px 4px -2px rgba(0,0,0,0.24)",
    });

    const visibleIntegrations = orgIntegrationData.filter(
        (i) =>
            i.ic_activeflag === 1 &&
            permittedContentTypeNames.has(i.integration_name)
    );
    return (
        <Grid container item xs={12} padding={2} style={{ marginTop: "-10px" }}>
            <Card
                style={{
                    overflow: "auto", padding: "10px", width: "100%",
                    boxShadow: 'rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset'
                }}
            >
                <Grid container spacing={2} sx={{ marginTop: "-10px" }}>

                    {/* ── "Add Integration" button: always visible regardless of active integrations ── */}
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Stack direction="row" display="flex" justifyContent="flex-start" alignItems="center">
                            <Fab variant="extended" size="small" onClick={() => setAddIntegrationDialog(true)}
                                sx={{
                                    backgroundColor: "#003366", color: "#fff", textTransform: "none", transition: "0.3s ease-in-out",
                                    "&:hover": { backgroundColor: "#0d45a5", color: "#fff" }, marginLeft: "10px"
                                }}
                            >
                                <AddIcon sx={{ fontSize: "20px", mr: 0.5 }} />
                                Add Integration
                            </Fab>
                        </Stack>
                    </Grid>

                    {/* ── Integration cards or empty-state message, always below the button ── */}
                    {visibleIntegrations.length > 0 ? (
                        visibleIntegrations.map(
                            ({ ic_org_integration_id, integration_name, integration_description }, index) => {
                                const isCredentialExists = credentialData.some(
                                    (data) => data.org_integration_id === ic_org_integration_id
                                );

                                return (
                                    <Grid item xs={12} sm={12} md={6} lg={4} xl={4} key={index}>
                                        <StyledCard>
                                            <Stack
                                                direction="row"
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <DashboardIcon
                                                    fontSize="large"
                                                    sx={{ marginLeft: "12px", color: "#003366" }}
                                                />

                                                {isCredentialExists && (
                                                    <IconButton
                                                        sx={{ color: "#003366" }}
                                                        onClick={() =>
                                                            handleUpdateIntegrationDialog({
                                                                ic_org_integration_id,
                                                                integration_name
                                                            })
                                                        }
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                )}
                                            </Stack>

                                            <h3 style={{ textAlign: "left", margin: "25px 0 5px 10px" }}>
                                                {integration_name}
                                            </h3>

                                            <Typography
                                                style={{
                                                    fontSize: "0.85rem",
                                                    textAlign: "left",
                                                    margin: "-5px 0 0 10px",
                                                    lineHeight: "1.2rem"
                                                }}
                                            >
                                                <em>{integration_description}</em>
                                            </Typography>
                                        </StyledCard>
                                    </Grid>
                                );
                            }
                        )
                    ) : (
                        <Grid item xs={12}>
                            <Typography>
                                <em>No records to display</em>
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Card>

            {/* ── Add Integration Dialog ── */}
            <Dialog open={addIntegrationDialog} onClose={hanldeCloseIntegrationDialog} fullWidth maxWidth="sm" disableEscapeKeyDown TransitionComponent={Transition}>
                <DialogTitle sx={{ m: 0, p: 1, pl: 2, pr: 2, color: "white", backgroundColor: "#003366" }}>
                    Integrations
                    <IconButton aria-label="close" onClick={hanldeCloseIntegrationDialog} style={{ float: "right", color: "gray" }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>

                        {/* ── Application Name: permitted first, non-permitted greyed out with tooltip ── */}
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <Autocomplete
                                size="small"
                                options={integrationOptions}
                                getOptionLabel={(option) => option?.label ?? ""}
                                getOptionDisabled={(option) => option.disabled}
                                isOptionEqualToValue={(option, value) => option.label === value?.label}
                                onChange={(_, newValue) => {
                                    setApplicationName(newValue?.label ?? "");
                                    setApplicationNameError("");
                                    if (newValue) {
                                        setSelectedOrgIntegrationId(newValue.ic_org_integration_id);
                                        const existing = credentialData?.find(
                                            (data) => data.org_integration_id === newValue.ic_org_integration_id
                                        );
                                        setUsername(existing?.username ?? "");
                                        setPassword(existing?.password ?? "");
                                    } else {
                                        setSelectedOrgIntegrationId("");
                                        setUsername("");
                                        setPassword("");
                                    }
                                }}
                                renderOption={(props, option, { index }) => {
                                    // Tooltip wraps a <span> (not the <li> directly) so it fires
                                    // even when pointer-events are disabled on the list item.
                                    const isFirstDisabled =
                                        option.disabled &&
                                        (index === 0 || !integrationOptions[index - 1]?.disabled);

                                    return (
                                        <React.Fragment key={option.label}>
                                            {isFirstDisabled && (
                                                <div style={{ borderTop: "0.5px solid #afa1a1", margin: "2px 0" }} />
                                            )}
                                            <ToolTip
                                                key={option.label}
                                                title={option.disabled ? option.disabledReason : ""}
                                                placement="right-end"
                                                arrow
                                                disableHoverListener={!option.disabled}
                                                disableTouchListener={!option.disabled}
                                            >
                                                <span style={{ display: "block", width: "100%" }}>
                                                    <li
                                                        {...props}
                                                        style={{
                                                            ...props.style,
                                                            color: option.disabled ? "#574d4d" : "inherit",
                                                            cursor: option.disabled ? "not-allowed" : "pointer",
                                                            pointerEvents: option.disabled ? "none" : "auto",
                                                            opacity: option.disabled ? 0.6 : 1,
                                                        }}
                                                    >
                                                        {option.label}
                                                    </li>
                                                </span>
                                            </ToolTip>
                                        </React.Fragment>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Application Name"
                                        required
                                        error={!!applicationNameError}
                                        helperText={applicationNameError}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <TextField variant="outlined" label="Username" value={username} size="small" required autoComplete="off" sx={{ width: "100%" }}
                                error={!!usernameError} helperText={usernameError} onChange={(e) => {
                                    setUsername(e.target.value);
                                    setUsernameError("");
                                }} />
                        </Grid>

                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <FormControl sx={{ width: '100%' }} variant="outlined" size='small' required error={!!passwordError}>
                                <InputLabel htmlFor="add-user-password">Password</InputLabel>
                                <OutlinedInput
                                    id="add-user-password"
                                    type={showPassword ? 'text' : 'password'}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword((show) => !show)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label="Password" value={password} onChange={(e) => {
                                        setPassword(e.target.value);
                                        setPasswordError("");
                                    }}
                                />
                                {passwordError && <FormHelperText>{passwordError}</FormHelperText>}
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ marginRight: "20px", position: "sticky", bottom: 0 }}>
                    <Button type="submit" variant="contained" size="small" onClick={(e) => handleSaveIntegrations(e)}
                        sx={{ textTransform: "none", bgcolor: "#003366", "&:hover": { bgcolor: "#0d45a5" } }}>
                        Save
                    </Button>
                    <Button variant="contained" size="small" color="error" sx={{ textTransform: "none" }} onClick={hanldeCloseIntegrationDialog}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* ── Update Integration Dialog (unchanged from original) ── */}
            <Dialog open={updateIntegrationDialog} onClose={hanldeCloseIntegrationDialog} fullWidth maxWidth="sm" disableEscapeKeyDown TransitionComponent={Transition}>
                <DialogTitle sx={{ m: 0, p: 1, pl: 2, pr: 2, color: "white", backgroundColor: "#003366" }}>
                    Edit Integrations
                    <IconButton aria-label="close" onClick={hanldeCloseIntegrationDialog} style={{ float: "right", color: "gray" }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <TextField variant="outlined" label="Application Name" size="small" value={applicationName} required disabled autoComplete="off" sx={{ width: "100%" }} />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <TextField variant="outlined" label="Username" size="small" value={username} required autoComplete="off" sx={{ width: "100%" }}
                                error={!!usernameError} helperText={usernameError} onChange={(e) => {
                                    setUsername(e.target.value);
                                    setUsernameError("");
                                }} />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <FormControl sx={{ width: '100%' }} variant="outlined" size='small' required error={!!passwordError}>
                                <InputLabel htmlFor="edit-user-password">Password</InputLabel>
                                <OutlinedInput
                                    id="edit-user-password"
                                    type={showPassword ? 'text' : 'password'}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword((show) => !show)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label="Password" value={password} onChange={(e) => {
                                        setPassword(e.target.value);
                                        setPasswordError("");
                                    }}
                                />
                                {passwordError && <FormHelperText>{passwordError}</FormHelperText>}
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ marginRight: "20px", position: "sticky", bottom: 0 }}>
                    <Button type="submit" variant="contained" size="small" onClick={(e) => handleUpdateIntegrations(e)}
                        sx={{ textTransform: "none", bgcolor: "#003366", "&:hover": { bgcolor: "#0d45a5" } }}>
                        Update
                    </Button>
                    <Button variant="contained" size="small" color="error" sx={{ textTransform: "none" }} onClick={hanldeCloseIntegrationDialog}>Cancel</Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default IntegrationList;